
import React, { useMemo, useState } from 'react'
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import useTranslation from '@/Hooks/UseTranslation';
import Card from '@/Components/Card/Card';

import './ProjectSCurve.scss'

interface Props {
  data: any
}

const ProjectSCurve = ({ data }: Props) => {

  const { t } = useTranslation()

  const [ activeSeries, setActiveSeries ] = useState<any>({
    baseline: true,
    scheduled: true,
    actual: true,
    projected: true,
  })

  const tooltipFormatter = (items: any) => {

    if (!items.length) return ''

    const firstItem = items[0]

    const weekNumber = firstItem.axisValueLabel

    const series: any[] = []

    items.map((item: any) => {

      if (item.value) {

        series.push(`
          <div class="tooltip-item">
            <div class="tooltip-item-color" style="background-color: ${item.color}"></div>
            <div class="tooltip-item-content">
              <div class="tooltip-item-label">${item.seriesName}</div>
              <div class="tooltip-item-value">${item.value ? item.value.toFixed(0) : ''}%</div>
            </div>
          </div>
        `)
      }
    })

      return `
        <div class="tooltip-content">
          <h5>${t('PROJECT_DASHBOARD:SCurveTooltipTitle') + ' ' + weekNumber}</h5>
          ${series.join('')}
        </div>
      `
  }

  const { series, totalWeeks } = useMemo(() => {

    const output: any[] = []

    let baseLine: any[] = []
    let scheduled: any[] = []
    let actual: any[] = []
    let projected: any[] = []

    const weeksNumbers: number[] = []
    let counter = 0

    for (const key in data) {

      const week = data[key]

      baseLine.push(week.baseLineAcc)
      scheduled.push(week.scheduledAcc)

      if (week.actualAcc) {
        actual.push(week.actualAcc)

        if (week.isCurrentWeek) {

          projected.push(week.projectAcc)
        } else {

          projected.push(null)
        }
      } else {
        actual.push(null)
        projected.push(week.projectAcc)
      }

      counter += 1

      weeksNumbers.push(counter)
    }


    if (activeSeries.baseline) {

      let maxBaseLine = 0

      baseLine.forEach((value: number) => {

        if (value > maxBaseLine)
          maxBaseLine = value
      })
      if (maxBaseLine > 0) {
        baseLine = baseLine.map(value => value ? Number(((value / maxBaseLine) * 100).toFixed(0)) : '')
      }

      output.push({
        data: baseLine,
        name: t('PROJECT_DASHBOARD:SCurveBaseLineLabel'),
        type: 'line',
        smooth: true,
        symbol: 'none',
        lineStyle: {
          normal: {
            color: '#006ebc',
            width: 3,
          }
        },
        itemStyle: {
          color: '#006ebc',
        },
        areaStyle: {
          opacity: 0.8,
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            {
              offset: 0,
              color: '#006ebcdb'
            },
            {
              offset: 1,
              color: '#fff'
            }
          ])
        },
      })
    }

    if (activeSeries.scheduled) {

      let maxScheduled = 0

      scheduled.forEach((value: number) => {

        if (value > maxScheduled)
          maxScheduled = value
      })

      if (maxScheduled > 0) {
        scheduled = scheduled.map(value => value ? Number(((value / maxScheduled) * 100).toFixed(0)) : '')
      }

      output.push({
        data: scheduled,
        name: t('PROJECT_DASHBOARD:SCurveScheduledLabel'),
        type: 'line',
        smooth: true,
        symbol: 'none',
        lineStyle: {
          normal: {
            color: '#34a854',
            width: 4,
            type: 'solid'
          }
        },
        itemStyle: {
          color: '#34a854',
        },
        areaStyle: {
          opacity: 0.8,
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            {
              offset: 0,
              color: '#34a85490'
            },
            {
              offset: 1,
              color: '#fff'
            }
          ])
        },
      })
    }

    let maxProjected = 0
    if (activeSeries.actual) {


      projected.forEach((value: number) => {

        if (value > maxProjected)
          maxProjected = value
      })

      if (maxProjected) {
        actual = actual.map(value => value ? Number(((value / maxProjected) * 100).toFixed(0)) : '')
      }

      output.push({
        data: actual,
        name: t('PROJECT_DASHBOARD:SCurveActualLabel'),
        type: 'line',
        smooth: true,
        symbol: 'none',
        lineStyle: {
          normal: {
            color: '#FF6F00',
            width: 4,
          }
        },
        itemStyle: {
          color: '#FF6F00',
        },
        areaStyle: {
          opacity: 0.8,
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            {
              offset: 0,
              color: '#FF6F0090'
            },
            {
              offset: 1,
              color: '#fff'
            }
          ])
        },
      })
    }

    if (activeSeries.projected) {

      if (maxProjected > 0) {
        projected = projected.map(value => value ? Number(((value / maxProjected) * 100).toFixed(0)) : '')
      }

      output.push({
        data: projected,
        name: t('PROJECT_DASHBOARD:SCurveProjectedLabel'),
        type: 'line',
        smooth: true,
        symbol: 'none',
        lineStyle: {
          normal: {
            color: '#f7a464',
            width: 4,
            type: 'dashed'
          }
        },
        itemStyle: {
          color: '#f7a464',
        },
        areaStyle: {
          opacity: 0.8,
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            {
              offset: 0,
              color: '#f7a46490'
            },
            {
              offset: 1,
              color: '#fff'
            }
          ])
        },
      })
    }

    return { series: output, totalWeeks: weeksNumbers}
  }, [data, activeSeries.baseline, activeSeries.scheduled, activeSeries.actual, activeSeries.projected])

  const option = useMemo(() => {

    return {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        animation: true
      },
      formatter: tooltipFormatter
    },
    legend: {
      bottom: 0,
      data: series.map(s => s.name)
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: totalWeeks,
      axisLabel: {
        formatter: "Sem {value}"
      },
    },
    yAxis: {
      axisLabel: {
        formatter: "{value}"
      },
      type: 'value'
    },
    series,
  }
  } , [series, totalWeeks])

  const handleToggleSerie = (serie: string) => {

    setActiveSeries({
      ...activeSeries,
      [serie]: !activeSeries[serie],
    })
  }

  return (
    <Card
      title={t('PROJECT_DASHBOARD:SCurveTitle')}
      buttons={[
        {
          icon: <span className="icon-linea-basic-chart-line"></span>,
          label: t('PROJECT_DASHBOARD:SCurveBaseLineLabel'),
          shortLabel: t('PROJECT_DASHBOARD:SCurveBaseLineShortLabel'),
          className: 'baseline',
          onClick: () => handleToggleSerie('baseline'),
          visible: true,
          active: activeSeries.baseline,
        },
        {
          icon: <span className="icon-linea-basic-chart-line"></span>,
          label: t('PROJECT_DASHBOARD:SCurveScheduledLabel'),
          shortLabel: t('PROJECT_DASHBOARD:SCurveScheduledShortLabel'),
          className: 'scheduled',
          onClick: () => handleToggleSerie('scheduled'),
          visible: true,
          active: activeSeries.scheduled,
        },
        {
          icon: <span className="icon-linea-basic-chart-line"></span>,
          label: t('PROJECT_DASHBOARD:SCurveActualLabel'),
          shortLabel: t('PROJECT_DASHBOARD:SCurveActualShortLabel'),
          className: 'actual',
          onClick: () => handleToggleSerie('actual'),
          visible: true,
          active: activeSeries.actual,
        },
        {
          icon: <span className="icon-linea-basic-chart-line"></span>,
          label: t('PROJECT_DASHBOARD:SCurveProjectedLabel'),
          shortLabel: t('PROJECT_DASHBOARD:SCurveProjectedShortLabel'),
          className: 'projected',
          onClick: () => handleToggleSerie('projected'),
          visible: true,
          active: activeSeries.projected,
        },
      ]}
    >
      <ReactEChartsCore
        echarts={echarts}
        option={option}
        notMerge={true}
        style={{ height: '420px', width: '100%' }}
      />
    </Card>
  )
}

export default ProjectSCurve
